import { slugify } from "@/helpers/format.js";
import { snakeToCamel } from "@xs2event/js-lib/util";
import { lookupSportTypeName, useSportTypesStore } from "@/store/sportTypes.js";

export const toEvent = ({ slug, event_id }) => "/" + (slug || event_id);

export const getPath = (event) => {
  if (event) {
    const _event = snakeToCamel(event);

    const { sportType, hometeamName, visitingName, tournamentName, season, tournamentId } = _event;

    const path = [
      { label: "Home", to: "/" },
      {
        label: lookupSportTypeName(sportType),
        to: { path: "/events", query: { sport_type: sportType === "soccer" ? "football" : sportType } },
      },
    ];

    // Check if there's a team or tournament filter in stored query params and add appropriate step
    if (typeof window !== "undefined") {
      try {
        const EVENTS_QUERY_STORAGE_KEY = "events-page-query-params";
        const storedQuery = sessionStorage.getItem(EVENTS_QUERY_STORAGE_KEY);
        if (storedQuery) {
          const queryParams = JSON.parse(storedQuery);
          const sportTypeValue = sportType === "soccer" ? "football" : sportType;

          // Keep tournament in breadcrumb even when user arrived from team/search flows
          // where no tournament query was present in storage.
          const tournamentFilter = queryParams.tournament;
          const tournamentLabel = tournamentName ? `${tournamentName}${season ? ` (${season})` : ""}` : null;
          let resolvedTournamentSlug = tournamentFilter;

          if (!resolvedTournamentSlug && tournamentName) {
            const sportTypesStore = useSportTypesStore();
            const allTournaments = sportTypesStore.sport_types.flatMap((x) => x.tournaments || []);

            const matchedTournament = allTournaments.find((t) => {
              const sameId = tournamentId && (t.tournament_id === tournamentId || t.tournamentId === tournamentId);
              const sameName = t.official_name === tournamentName || t.officialName === tournamentName;
              const sameSeason = !season || t.season === season;
              const sameSportType = !sportType || t.sport_type === sportType;
              return sameId || (sameName && sameSeason && sameSportType);
            });

            resolvedTournamentSlug = matchedTournament?.slug || slugify(`${tournamentName} ${season || ""}`.trim());
          }

          if (tournamentLabel) {
            if (resolvedTournamentSlug) {
              path.push({
                label: tournamentLabel,
                to: {
                  path: "/events",
                  query: {
                    tournament: resolvedTournamentSlug,
                    sport_type: sportTypeValue,
                  },
                },
              });
            } else {
              path.push({ label: tournamentLabel });
            }
          }

          // Check for team filter (only if no tournament filter, or if both are present)
          const teamFilter = queryParams.team;
          if (teamFilter && (hometeamName || visitingName)) {
            let teamName = null;

            // Check if the filter matches home team or visiting team
            // Handle both slug format (from URL) and full name format
            const teamFilterLower = teamFilter.toLowerCase();
            const homeTeamSlug = hometeamName ? slugify(hometeamName) : null;
            const visitingTeamSlug = visitingName ? slugify(visitingName) : null;

            if (hometeamName) {
              if (
                teamFilter === hometeamName ||
                teamFilterLower === hometeamName.toLowerCase() ||
                teamFilterLower === homeTeamSlug
              ) {
                teamName = hometeamName;
              }
            }

            if (!teamName && visitingName) {
              if (
                teamFilter === visitingName ||
                teamFilterLower === visitingName.toLowerCase() ||
                teamFilterLower === visitingTeamSlug
              ) {
                teamName = visitingName;
              }
            }

            // If we found a matching team, add it as a breadcrumb step
            if (teamName) {
              const query = {
                team: slugify(teamName), // Use slug format for URL consistency
                sport_type: sportTypeValue,
              };

              // Include tournament in query if it was filtered
              if (tournamentFilter) {
                query.tournament = tournamentFilter;
              }

              path.push({
                label: teamName,
                to: {
                  path: "/events",
                  query,
                },
              });
            }
          }
        }
      } catch (e) {
        // Print error to help with debugging
        console.error("Error parsing stored query in getPath:", e);
        // Ignore errors parsing stored query
      }
    }

    path.push({ label: _event.eventName });
    return path;
  } else {
    return [{ label: "Home", to: "/" }, { label: "Loading..." }];
  }
};
